import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  MapPin,
  FileText,
  Banknote,
  ShieldCheck,
  Truck,
  RotateCcw,
  Lock,
  Plus,
  X,
  Phone,
  Building2,
  Sparkles,
  CheckCircle2,
  Loader2,
  AlertCircle,
  ShoppingBag,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";
import * as ordersApi from "../api/ordersApi";
import * as profileApi from "../api/profileApi";
import * as locationsApi from "../api/locationsApi";
import { formatMoney, formatProductSize, normalizeEgyptianPhone, validateEgyptianPhone, formatPhoneForDisplay } from "../utils/format";
import { resolveImageUrl } from "../utils/images";
import SearchableSelect from "../components/SearchableSelect";
import CouponBox from "../components/CouponBox";
import { FREE_SHIPPING_THRESHOLD } from "../constants/shipping";
import "./AccountForms.css";
import "./Checkout.css";

const PAYMENT_METHODS = [
  {
    value: "CashOnDelivery",
    labelKey: "checkout.cod",
    subKey: "checkout.codSub",
    icon: Banknote,
    badge: null,
  },
];

const emptyAddressForm = {
  country: "Egypt",
  cityId: "",
  districtId: "",
  description: "",
  phoneNumber: "",
  isDefault: false,
};

// Component for displaying the selected address card
function SelectedAddressCard({ address, onChooseDifferent, onAddNew, t, isRTL }) {
  if (!address) return null;
  return (
    <div className="checkout-address-selected">
      <div className="checkout-address-selected__badge">
        <Check size={13} strokeWidth={2.5} />
        <span>{t("checkout.selectedBadge")}</span>
      </div>

      <div className="checkout-address-selected__body">
        <div className="checkout-address-selected__pin">
          <MapPin size={18} />
        </div>
        <div className="checkout-address-selected__details">
          <h4 className="checkout-address-selected__location">
            {address.districtName ? `${address.districtName}، ` : ""}
            {address.cityName}، {address.country}
          </h4>
          <p className="checkout-address-selected__desc">{address.description}</p>
          <div className="checkout-address-selected__phone">
            <Phone size={13} />
            <span dir="ltr">{formatPhoneForDisplay(address.phoneNumber)}</span>
          </div>
        </div>
      </div>

      <div className="checkout-address-selected__actions">
        <button
          type="button"
          className="btn-outline checkout-addr-btn"
          onClick={onChooseDifferent}
        >
          {t("checkout.differentAddress")}
        </button>
        <button
          type="button"
          className="btn-ghost checkout-addr-btn"
          onClick={onAddNew}
        >
          <Plus size={14} />
          <span>{t("checkout.addNewAddress")}</span>
        </button>
      </div>
    </div>
  );
}


export default function Checkout() {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { t, isRTL, language } = useLanguage();
  const { appliedCoupon, discountAmount, removeCoupon, isOverPackageLimit, maxPackageVolume } = useCart();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [calculatingShipping, setCalculatingShipping] = useState(false);

  const [shippingId, setShippingId] = useState("");
  const [billingId, setBillingId] = useState("");
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("CashOnDelivery");

  // Modal State
  // modalMode: "select-shipping", "select-billing", "add-shipping", "add-billing"
  const [modalMode, setModalMode] = useState(null);
  const [addressForm, setAddressForm] = useState(emptyAddressForm);
  const [savingAddress, setSavingAddress] = useState(false);

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    async function loadCities() {
      try {
        const res = await locationsApi.getCities();
        setCities(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error("Could not load cities", e);
      }
    }
    loadCities();
  }, []);

  useEffect(() => {
    async function loadDistricts() {
      if (!addressForm.cityId) {
        setDistricts([]);
        return;
      }
      try {
        const res = await locationsApi.getDistricts(addressForm.cityId);
        setDistricts(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error("Could not load districts", e);
      }
    }
    loadDistricts();
  }, [addressForm.cityId]);

  const loadSummary = async (addrId) => {
    try {
      const data = await ordersApi.getCheckoutSummary(userId, addrId);
      setSummary(data);
      return data;
    } catch (e) {
      addToast(e.message || "Could not load checkout.", "error");
      throw e;
    }
  };

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);

    async function initCheckout() {
      try {
        const data = await ordersApi.getCheckoutSummary(userId);
        if (cancelled) return;

        const def =
          typeof data?.defaultUserAddress === "string"
            ? data.defaultUserAddress
            : data?.defaultUserAddress?.id;
        const first = data?.addresses?.[0]?.id;
        const pick = def || first || "";

        setShippingId(pick);
        setBillingId(pick);

        // If backend already returned shippingFees for the resolved address, save it.
        // Otherwise, if pick exists and shipping fees were not yet calculated, fetch with pick!
        if (
          pick &&
          (data?.shippingFees === undefined ||
            data?.shippingFees === null ||
            (data?.shippingFees === 0 &&
              (data?.cart?.totalPrice || 0) <
                (data?.freeShippingThreshold || FREE_SHIPPING_THRESHOLD)))
        ) {
          const withShipping = await ordersApi.getCheckoutSummary(userId, pick);
          if (!cancelled) {
            setSummary(withShipping);
          }
        } else {
          setSummary(data);
        }
      } catch (e) {
        if (!cancelled) {
          addToast(e.message || "Could not load checkout.", "error");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    initCheckout();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const addresses = summary?.addresses || [];
  const cart = summary?.cart;
  const items = cart?.items || [];
  const shippingFees = Number(summary?.shippingFees) || 0;
  const cartTotal = Number(cart?.totalPrice) || 0;
  const discountedTotal = Math.max(0, cartTotal - (discountAmount || 0));
  const grandTotal = discountedTotal + shippingFees;

  const getAddressById = (id) => addresses.find((a) => a.id === id);
  const selectedShipping = getAddressById(shippingId);
  const selectedBilling = getAddressById(billingId);

  const handleSelectShippingAddress = async (newId) => {
    setShippingId(newId);
    if (sameAsShipping) {
      setBillingId(newId);
    }
    setModalMode(null);
    setCalculatingShipping(true);
    try {
      await loadSummary(newId);
    } catch (e) {
      // Ignored
    } finally {
      setCalculatingShipping(false);
    }
  };

  const handleSelectBillingAddress = (newId) => {
    setBillingId(newId);
    setModalMode(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalBillingId = sameAsShipping ? shippingId : billingId;

    if (!shippingId) {
      addToast(t("checkout.missingShipping"), "error");
      return;
    }
    if (!sameAsShipping && !finalBillingId) {
      addToast(t("checkout.missingBilling"), "error");
      return;
    }

    if (isOverPackageLimit) {
      addToast(t("cart.mustReduceItemsToCheckout"), "error");
      return;
    }

    setSubmitting(true);
    try {
      const res = await ordersApi.placeOrder(userId, {
        shippingAddressId: shippingId,
        billingAddressId: finalBillingId,
        paymentMethod,
        couponCode: appliedCoupon?.code || undefined,
      });
      const url = res?.url || res?.Url;
      if (url && typeof url === "string") {
        removeCoupon();
        window.location.href = url;
        return;
      }
      addToast("Order placed but no redirect URL was returned.", "error");
    } catch (err) {
      addToast(err.message || "Checkout failed.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveNewAddress = async (e) => {
    e.preventDefault();
    setSavingAddress(true);
    try {
      if (!validateEgyptianPhone(addressForm.phoneNumber)) {
        addToast(
          isRTL
            ? "يرجى إدخال رقم محمول مصري صحيح (مثال: 010xxxxxxxx أو 10xxxxxxxx)."
            : "Please enter a valid Egyptian mobile number (e.g., 010xxxxxxxx or 10xxxxxxxx).",
          "error"
        );
        setSavingAddress(false);
        return;
      }

      const phone = normalizeEgyptianPhone(addressForm.phoneNumber);

      const body = {
        country: addressForm.country,
        cityId: addressForm.cityId,
        districtId: addressForm.districtId,
        description: addressForm.description.trim(),
        phoneNumber: phone,
        isDefault: addressForm.isDefault,
      };

      const newAddress = await profileApi.createAddress(userId, body);
      addToast(isRTL ? "تمت إضافة العنوان بنجاح" : "Address added successfully", "success");

      const createdId = newAddress?.id || newAddress?.Id;
      setCalculatingShipping(true);
      const updatedData = await loadSummary(createdId);
      const newId =
        createdId || updatedData?.addresses?.[updatedData?.addresses?.length - 1]?.id;

      if (modalMode === "add-shipping") {
        setShippingId(newId);
        if (sameAsShipping) setBillingId(newId);
      } else if (modalMode === "add-billing") {
        setBillingId(newId);
      }

      setModalMode(null);
    } catch (err) {
      addToast(err.message || "Failed to add address.", "error");
    } finally {
      setCalculatingShipping(false);
      setSavingAddress(false);
    }
  };

  const openAddModal = (mode) => {
    setAddressForm(emptyAddressForm);
    setModalMode(mode);
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="page-loading">
          <span className="page-loading__dot" />
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container checkout-inner">
        {/* Checkout Step Breadcrumbs matching Cart */}
        <nav className="cart-steps" aria-label="Checkout flow">
          <Link
            to="/cart"
            className="cart-step cart-step--completed"
            title={t("checkout.backToCart")}
          >
            <span className="cart-step__num">
              <Check size={13} strokeWidth={2.5} />
            </span>
            <span className="cart-step__label">{t("cart.stepBag")}</span>
          </Link>
          <span className="cart-steps__divider" />
          <div className="cart-step cart-step--active" aria-current="step">
            <span className="cart-step__num">2</span>
            <span className="cart-step__label">{t("cart.stepCheckout")}</span>
          </div>
          <span className="cart-steps__divider" />
          <div className="cart-step cart-step--inactive">
            <span className="cart-step__num">3</span>
            <span className="cart-step__label">{t("cart.stepDone")}</span>
          </div>
        </nav>

        {/* Compact Integrated Page Header */}
        <div className="checkout-header">
          <div className="checkout-header__title-group">
            <h1 className="checkout-header__title">{t("checkout.title")}</h1>
            <p className="checkout-header__subtitle">{t("checkout.subtitle")}</p>
          </div>
          <Link to="/cart" className="checkout-header__back-link">
            {isRTL ? <ArrowRight size={15} /> : <ArrowLeft size={15} />}
            <span>{t("checkout.backToCart")}</span>
          </Link>
        </div>

        {items.length === 0 ? (
          <div className="checkout-empty-banner">
            <p>{t("checkout.emptyCart")}</p>
            <Link to="/shop" className="btn-primary">
              <span>{t("checkout.continueShopping")}</span>
              <ArrowRight size={14} className="rtl-flip" />
            </Link>
          </div>
        ) : (
          <>
            {isOverPackageLimit && (
              <div className="cart-package-limit-alert" role="alert" style={{ marginBottom: "24px" }}>
                <div className="cart-package-limit-alert__icon">
                  <AlertCircle size={22} />
                </div>
                <div className="cart-package-limit-alert__content">
                  <h3 className="cart-package-limit-alert__title">
                    {t("cart.packageLimitExceededBannerTitle")}
                  </h3>
                  <p className="cart-package-limit-alert__desc">
                    {t("cart.packageLimitExceededBannerDesc").replace(
                      "{maxVolume}",
                      maxPackageVolume.toLocaleString()
                    )}
                  </p>
                  <div style={{ marginTop: "12px" }}>
                    <Link
                      to="/cart"
                      className="btn-outline"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "7px 16px",
                        fontSize: "13px",
                        textDecoration: "none",
                      }}
                    >
                      <ShoppingBag size={14} />
                      <span>{t("cart.title")}</span>
                      {isRTL ? <ArrowLeft size={14} /> : <ArrowRight size={14} />}
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
            <div className="checkout-layout">
              {/* Main Checkout Column */}
              <div className="checkout-main-col">
                {/* 1. SHIPPING ADDRESS */}
                <section className="checkout-card">
                  <div className="checkout-card__head">
                    <div className="checkout-card__icon-wrap">
                      <MapPin size={18} />
                    </div>
                    <h2 className="checkout-card__title">
                      {t("checkout.shippingAddress")}
                    </h2>
                  </div>

                  {addresses.length === 0 ? (
                    <div className="checkout-empty-address">
                      <div className="checkout-empty-address__icon">
                        <MapPin size={28} strokeWidth={1.5} />
                      </div>
                      <p>{t("checkout.noAddressPrompt")}</p>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => openAddModal("add-shipping")}
                      >
                        <Plus size={15} />
                        <span>{t("checkout.addShippingAddress")}</span>
                      </button>
                    </div>
                  ) : (
                    <SelectedAddressCard
                      address={selectedShipping}
                      onChooseDifferent={() => setModalMode("select-shipping")}
                      onAddNew={() => openAddModal("add-shipping")}
                      t={t}
                      isRTL={isRTL}
                    />
                  )}
                </section>

                {/* 2. BILLING ADDRESS */}
                <section className="checkout-card">
                  <div className="checkout-card__head">
                    <div className="checkout-card__icon-wrap">
                      <FileText size={18} />
                    </div>
                    <h2 className="checkout-card__title">
                      {t("checkout.billingAddress")}
                    </h2>
                  </div>

                  <div className="checkout-toggle-row">
                    <span className="checkout-toggle-label">
                      {t("checkout.billingSameAsShipping")}
                    </span>
                    <label
                      className="ui-toggle"
                      aria-label={t("checkout.billingSameAsShipping")}
                    >
                      <input
                        type="checkbox"
                        checked={sameAsShipping}
                        onChange={(e) => {
                          const val = e.target.checked;
                          setSameAsShipping(val);
                          if (val) setBillingId(shippingId);
                        }}
                      />
                      <span className="ui-toggle-slider" />
                    </label>
                  </div>

                  {!sameAsShipping && (
                    <div className="billing-address-reveal">
                      {addresses.length === 0 ? (
                        <div className="checkout-empty-address">
                          <p>{t("checkout.noAddressPrompt")}</p>
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => openAddModal("add-billing")}
                          >
                            <Plus size={15} />
                            <span>{t("checkout.addBillingAddress")}</span>
                          </button>
                        </div>
                      ) : (
                        <SelectedAddressCard
                          address={selectedBilling}
                          onChooseDifferent={() => setModalMode("select-billing")}
                          onAddNew={() => openAddModal("add-billing")}
                          t={t}
                          isRTL={isRTL}
                        />
                      )}
                    </div>
                  )}
                </section>

                {/* 3. PAYMENT METHOD */}
                <section className="checkout-card">
                  <div className="checkout-card__head">
                    <div className="checkout-card__icon-wrap">
                      <Banknote size={18} />
                    </div>
                    <h2 className="checkout-card__title">
                      {t("checkout.paymentMethod")}
                    </h2>
                  </div>

                  <div
                    className="checkout-payment-grid"
                    role="radiogroup"
                    aria-label={t("checkout.paymentMethod")}
                  >
                    {PAYMENT_METHODS.map((method) => {
                      const Icon = method.icon;
                      const isSelected = paymentMethod === method.value;
                      const badgeText = method.badge || (isRTL ? "متاح في مصر" : "Egypt");
                      return (
                        <div
                          key={method.value}
                          role="radio"
                          aria-checked={isSelected}
                          tabIndex={0}
                          className={`checkout-payment-card ${
                            isSelected ? "checkout-payment-card--selected" : ""
                          }`}
                          onClick={() => setPaymentMethod(method.value)}
                          onKeyDown={(e) => {
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                              setPaymentMethod(method.value);
                            }
                          }}
                        >
                          <div className="checkout-payment-card__radio">
                            <span
                              className={`checkout-radio-dot ${
                                isSelected ? "checkout-radio-dot--active" : ""
                              }`}
                            />
                          </div>
                          <div className="checkout-payment-card__icon-wrap">
                            <Icon size={22} />
                          </div>
                          <div className="checkout-payment-card__content">
                            <div className="checkout-payment-card__title-row">
                              <span className="checkout-payment-card__title">
                                {t(method.labelKey)}
                              </span>
                              {badgeText && (
                                <span className="checkout-payment-card__badge">
                                  {badgeText}
                                </span>
                              )}
                            </div>
                            <p className="checkout-payment-card__sub">
                              {t(method.subKey)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>

              {/* Order Summary Sticky Sidebar */}
              <aside className="checkout-aside">
                <div className="checkout-aside__header">
                  <h3 className="checkout-aside__title">
                    {t("checkout.orderSummary")}
                  </h3>
                  <span className="checkout-aside__badge">
                    {items.length}{" "}
                    {isRTL
                      ? items.length === 1
                        ? "قطعة"
                        : "قطع"
                      : items.length === 1
                      ? "item"
                      : "items"}
                  </span>
                </div>

                <ul className="checkout-lines">
                  {items.map((line) => {
                    const thumb = line.colorVariant?.url
                      ? resolveImageUrl(line.colorVariant.url)
                      : null;
                    const rawSize = line.sizeVariant?.size;
                    const formattedSize = rawSize
                      ? formatProductSize(rawSize, language)
                      : null;
                    const variantText = [formattedSize, line.colorVariant?.color]
                      .filter(Boolean)
                      .join(" · ");
                    const title =
                      isRTL && line.product?.arabicName
                        ? line.product.arabicName
                        : line.product?.name || (isRTL ? "منتج" : "Product");
                    const unitPrice = Number(line.product?.basePrice) || 0;
                    const lineTotal = unitPrice * line.quantity;

                    return (
                      <li key={line.id} className="checkout-line">
                        <div
                          className="checkout-line__thumb"
                          style={
                            thumb
                              ? { backgroundImage: `url(${thumb})` }
                              : {
                                  backgroundColor:
                                    line.colorVariant?.colorHex || "var(--cream)",
                                }
                          }
                        />
                        <div className="checkout-line__details">
                          <span className="checkout-line__name">{title}</span>
                          {variantText && (
                            <span className="checkout-line__variant">
                              {variantText}
                            </span>
                          )}
                          <div className="checkout-line__footer">
                            <span className="checkout-line__qty">
                              ×{line.quantity}
                            </span>
                            <span className="checkout-line__price">
                              {formatMoney(lineTotal, language)}
                            </span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <div className="checkout-breakdown">
                  <div className="checkout-breakdown__row">
                    <span>{t("cart.subtotal")}</span>
                    <span>{formatMoney(cartTotal, language)}</span>
                  </div>

                  {appliedCoupon && discountAmount > 0 && (
                    <div className="checkout-breakdown__row checkout-breakdown__row--discount">
                      <span className="checkout-breakdown__discount-label">
                        <span>{t("coupon.discount")}</span>
                        <span className="checkout-breakdown__coupon-code">({appliedCoupon.code})</span>
                      </span>
                      <span className="checkout-breakdown__discount-val">
                        -{formatMoney(discountAmount, language)}
                      </span>
                    </div>
                  )}

                  <div className="checkout-breakdown__row">
                    <span className="checkout-breakdown__shipping-label">
                      <Truck size={14} />
                      <span>{t("cart.shipping")}</span>
                    </span>
                    <span>
                      {calculatingShipping ? (
                        <Loader2
                          size={14}
                          className="spinner"
                          style={{ animation: "spin 1.5s linear infinite" }}
                        />
                      ) : shippingFees === 0 ? (
                        <span className="checkout-free-shipping">
                          {t("product.freeDelivery")}
                        </span>
                      ) : (
                        formatMoney(shippingFees, language)
                      )}
                    </span>
                  </div>
                  <div className="checkout-breakdown__total-row">
                    <span>{t("cart.total")}</span>
                    <strong>{formatMoney(grandTotal, language)}</strong>
                  </div>
                </div>

                {/* Anti-Churn Collapsible Promo Code Accordion */}
                <div className="checkout-coupon-section">
                  <CouponBox customCartTotal={cartTotal} />
                </div>

                {isOverPackageLimit && (
                  <div className="cart-summary__limit-warning" role="alert" style={{ marginBottom: "14px" }}>
                    <AlertCircle size={15} />
                    <span>{t("cart.mustReduceItemsToCheckout")}</span>
                  </div>
                )}

                <button
                  type="submit"
                  className={`btn-primary checkout-pay-btn ${isOverPackageLimit ? "cart-summary__checkout-btn--disabled" : ""}`}
                  disabled={
                    submitting ||
                    isOverPackageLimit ||
                    addresses.length === 0 ||
                    !shippingId ||
                    (!sameAsShipping && !billingId)
                  }
                  title={isOverPackageLimit ? t("cart.mustReduceItemsToCheckout") : undefined}
                >
                  {submitting ? (
                    <>
                      <Loader2
                        size={16}
                        className="spinner"
                        style={{ animation: "spin 1.5s linear infinite" }}
                      />
                      <span>{t("checkout.processing")}</span>
                    </>
                  ) : (
                    <>
                      <Lock size={15} />
                      <span>{t("checkout.placeOrder")}</span>
                      <ArrowRight size={14} className="rtl-flip" />
                    </>
                  )}
                </button>

                {/* CRO Trust Badges */}
                <div className="checkout-trust-box">
                  <div className="checkout-trust-item">
                    <ShieldCheck size={16} className="checkout-trust-icon" />
                    <span>{t("checkout.trustSecure")}</span>
                  </div>
                  <div className="checkout-trust-item">
                    <Truck size={16} className="checkout-trust-icon" />
                    <span>{t("checkout.trustDelivery")}</span>
                  </div>
                  <div className="checkout-trust-item">
                    <RotateCcw size={16} className="checkout-trust-icon" />
                    <span>{t("checkout.trustReturns")}</span>
                  </div>
                </div>

                <Link to="/cart" className="checkout-back-link">
                  {isRTL ? <ArrowRight size={13} /> : <ArrowLeft size={13} />}
                  <span>{t("checkout.backToCart")}</span>
                </Link>
              </aside>
            </div>
          </form>
          </>
        )}
      </div>


      {/* MODAL OVERLAY: SELECT OR ADD ADDRESS */}
      {modalMode && (
        <div
          className="admin-modal-overlay checkout-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setModalMode(null)}
        >
          <div
            className="admin-modal checkout-modal"
            style={{
              maxWidth: modalMode.startsWith("add") ? "680px" : "560px",
              padding: "28px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="checkout-modal__header">
              <h3 className="checkout-modal__title">
                {modalMode.startsWith("select")
                  ? t("checkout.modalSelectTitle")
                  : t("checkout.modalAddTitle")}
              </h3>
              <button
                type="button"
                className="checkout-modal__close"
                onClick={() => setModalMode(null)}
                aria-label="Close dialog"
              >
                <X size={18} />
              </button>
            </div>

            {/* SELECT EXISTING ADDRESS */}
            {modalMode.startsWith("select") && (
              <div className="checkout-modal__select-content">
                <div className="checkout-modal-address-list">
                  {addresses.map((a) => {
                    const isCurrentlySelected =
                      modalMode === "select-shipping"
                        ? shippingId === a.id
                        : billingId === a.id;
                    return (
                      <div
                        key={a.id}
                        tabIndex={0}
                        className={`checkout-modal-address-card ${
                          isCurrentlySelected ? "selected" : ""
                        }`}
                        onClick={() => {
                          if (modalMode === "select-shipping") {
                            handleSelectShippingAddress(a.id);
                          } else {
                            handleSelectBillingAddress(a.id);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (modalMode === "select-shipping") {
                              handleSelectShippingAddress(a.id);
                            } else {
                              handleSelectBillingAddress(a.id);
                            }
                          }
                        }}
                      >
                        <div className="checkout-modal-address-card__radio">
                          <span
                            className={`checkout-radio-dot ${
                              isCurrentlySelected ? "checkout-radio-dot--active" : ""
                            }`}
                          />
                        </div>
                        <div className="checkout-modal-address-card__info">
                          <div className="checkout-modal-address-card__head">
                            <strong className="checkout-modal-address-card__loc">
                              {a.districtName ? `${a.districtName}، ` : ""}
                              {a.cityName}، {a.country}
                            </strong>
                            {a.isDefault && (
                              <span className="checkout-modal-badge">
                                {t("checkout.defaultBadge")}
                              </span>
                            )}
                          </div>
                          <p className="checkout-modal-address-card__desc">
                            {a.description}
                          </p>
                          <p className="checkout-modal-address-card__phone" dir="ltr">
                            {formatPhoneForDisplay(a.phoneNumber)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="checkout-modal__actions-bar">
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() =>
                      openAddModal(
                        modalMode === "select-shipping"
                          ? "add-shipping"
                          : "add-billing"
                      )
                    }
                  >
                    <Plus size={14} />
                    <span>{t("checkout.addNewAddress")}</span>
                  </button>
                </div>
              </div>
            )}

            {/* ADD NEW ADDRESS */}
            {modalMode.startsWith("add") && (
              <form className="account-form" onSubmit={handleSaveNewAddress}>
                <div className="auth-field">
                  <label>{t("checkout.country")}</label>
                  <select
                    value={addressForm.country}
                    onChange={(e) =>
                      setAddressForm((f) => ({ ...f, country: e.target.value }))
                    }
                  >
                    <option value="Egypt">
                      {isRTL ? "مصر (Egypt)" : "Egypt"}
                    </option>
                  </select>
                </div>

                <div className="form-two-col">
                  <div className="auth-field">
                    <label>{t("checkout.city")}</label>
                    <select
                      value={addressForm.cityId}
                      onChange={(e) => {
                        setAddressForm((f) => ({
                          ...f,
                          cityId: e.target.value,
                          districtId: "",
                        }));
                      }}
                      required
                    >
                      <option value="" disabled>
                        {t("checkout.selectCity")}
                      </option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} {c.nameAr ? `(${c.nameAr})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="auth-field">
                    <label>{t("checkout.district")}</label>
                    <SearchableSelect
                      options={districts}
                      value={addressForm.districtId}
                      onChange={(val) =>
                        setAddressForm((f) => ({ ...f, districtId: val }))
                      }
                      placeholder={t("checkout.selectDistrict")}
                      disabled={!addressForm.cityId || districts.length === 0}
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <label>{t("checkout.streetDesc")}</label>
                  <input
                    value={addressForm.description}
                    onChange={(e) =>
                      setAddressForm((f) => ({
                        ...f,
                        description: e.target.value,
                      }))
                    }
                    placeholder={t("checkout.streetPlaceholder")}
                    required
                  />
                </div>

                <div className="auth-field">
                  <label>{t("checkout.phone")}</label>
                  <div className="checkout-phone-input-wrap">
                    <div className="checkout-phone-prefix">+20</div>
                    <input
                      type="tel"
                      className="checkout-phone-input"
                      value={addressForm.phoneNumber}
                      onChange={(e) =>
                        setAddressForm((f) => ({
                          ...f,
                          phoneNumber: e.target.value,
                        }))
                      }
                      required
                      placeholder={t("checkout.phonePlaceholder")}
                    />
                  </div>
                </div>

                <label className="checkout-checkbox-label">
                  <input
                    type="checkbox"
                    checked={addressForm.isDefault}
                    onChange={(e) =>
                      setAddressForm((f) => ({
                        ...f,
                        isDefault: e.target.checked,
                      }))
                    }
                  />
                  <span>{t("checkout.setDefault")}</span>
                </label>

                <div className="admin-modal__actions checkout-modal__bottom-bar">
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => setModalMode(null)}
                  >
                    {t("action.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={savingAddress}
                  >
                    {savingAddress ? (
                      <>
                        <Loader2
                          size={14}
                          className="spinner"
                          style={{ animation: "spin 1.5s linear infinite" }}
                        />
                        <span>{t("checkout.savingAddress")}</span>
                      </>
                    ) : (
                      <span>{t("checkout.saveAddress")}</span>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
